import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { NextLink } from 'tradera/apps/nextweb/components/next-link';
import { updateEnvironmentHash } from 'tradera/state/environment/actions';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';
import { useLocation } from 'tradera/hooks/use-location';
import { isNextJs } from 'tradera/utils/nextjs';
import { useAppSelector } from 'tradera/state/hooks';
import { useNextWebSpaLink } from './use-next-web-spa-link';

const PRODUCTION_ORIGIN = 'https://www.tradera.com';
const getRelativeUrl = (url: URL) => `${url.pathname}${url.search}`;

type ALinkProps = React.ComponentPropsWithRef<'a'> & {
    useLink?: boolean;
};

const ALink = React.forwardRef<HTMLAnchorElement, ALinkProps>(function ALink(
    {
        useLink: isTouchwebSpaLink,
        href,
        children,
        onClick: givenOnClick,
        ...otherProps
    }: ALinkProps,
    forwardedRef
) {
    const dispatch = useDispatch();
    const location = useLocation();
    const isSpaNavigationEnabled = useAppSelector(
        (state) => state.environment.isSpaNavigationEnabled
    );
    const isNextWebAddUrlParamEnabled = useIsFeatureEnabled(
        'next-web-add-url-param'
    );
    const isTouchWebAddUrlParamEnabled = useIsFeatureEnabled(
        'touch-web-add-url-param'
    );
    const absoluteUrl = new URL(href ?? '', PRODUCTION_ORIGIN);

    if (isTouchWebAddUrlParamEnabled) {
        absoluteUrl.searchParams.set('next', '0');
    } else if (isNextWebAddUrlParamEnabled) {
        absoluteUrl.searchParams.set('next', '1');
    }

    const isNextWebSpaLink = useNextWebSpaLink(absoluteUrl);
    const handleSpaNavigationClick = useCallback(
        (evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            dispatch<any>(updateEnvironmentHash());
            if (givenOnClick) givenOnClick(evt);
        },
        [dispatch, givenOnClick]
    );

    const spaNavigationOrigins = [PRODUCTION_ORIGIN, location?.origin];
    const isRelativeUrl = spaNavigationOrigins.includes(absoluteUrl.origin);

    // Strip away origin for SPA links
    const relativeUrl = getRelativeUrl(absoluteUrl);

    if (isSpaNavigationEnabled && isRelativeUrl) {
        if (isNextJs && isNextWebSpaLink) {
            // NextJS routing
            return (
                <NextLink
                    href={relativeUrl}
                    passHref
                    onClick={handleSpaNavigationClick}
                    ref={forwardedRef}
                    {...otherProps}>
                    {children}
                </NextLink>
            );
        } else if (!isNextJs && !isNextWebSpaLink && isTouchwebSpaLink) {
            // react-router-dom
            return (
                <Link
                    to={relativeUrl}
                    {...otherProps}
                    onClick={handleSpaNavigationClick}
                    ref={forwardedRef}>
                    {/* @ts-ignore: TS2322 because types from react-router relies on some other React version. */}
                    {children}
                </Link>
            );
        }
    }

    const url = isRelativeUrl ? relativeUrl : absoluteUrl.toString();
    return (
        <a href={url} onClick={givenOnClick} {...otherProps} ref={forwardedRef}>
            {children}
        </a>
    );
});

// Deprecated, please use the named export instead
export default ALink;
